import {track as trackMossAnalytics} from '@getmoss/moss-analytics'
import {isFilled} from '@prismicio/helpers'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {AnimatePresence, motion} from 'framer-motion'
import dynamic from 'next/dynamic'
import type {TestimonialsSectionSliceDefault} from 'prismicio-types'
import type {FC} from 'react'
import {useCallback, useEffect, useState} from 'react'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {Gradient} from 'slices/SideGrid/components'
import type {TrackingProps} from 'src/common/helpers'
import {EVENTS, track} from 'src/common/helpers'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import type {Context} from 'src/common/types'
import {TestimonialContent} from './components/TestimonialContent'

const TestimonialBackground = dynamic(
  () => import('./TestimonialBackground').then((mod) => mod.TestimonialBackground),
  {
    ssr: false,
  },
)

const contentVariants = {
  initial: {opacity: 0},
  animate: {opacity: 1, transition: {delay: 0.4, duration: 0.7}, ease: 'easeInOut'},
  exit: {opacity: 0, transition: {duration: 0.5}},
}

/**
 * Props for `TestimonialsSection`.
 */
export type TestimonialsSectionProps = {
  slice: TestimonialsSectionSliceDefault
  context: Context
  index: number
}

/**
 * Component for "TestimonialsSection" Slices.
 */
const TestimonialsSectionDefault: FC<TestimonialsSectionProps> = ({slice, index: componentIndex, context}) => {
  const [activeTab, setActiveTab] = useState(0)
  const {testimonials, ratings_section} = slice.primary
  const domainCountry = useGetDomainCountry()

  const handleClick = useCallback(
    (index: number, trackingData: TrackingProps, newTrackingData: Record<string, string>) => {
      track(trackingData)
      setActiveTab(index)

      trackMossAnalytics({
        event: 'interaction',
        interaction: {
          location: `component-${componentIndex}-testimonials-${index}`,
          type: 'click',
          element: 'category-tab',
          elementText: newTrackingData.elementText,
        },
        page: {
          domainCountry,
          type: context.pageUid || 'unknown',
        },
      })
    },
    [componentIndex, domainCountry, context.pageUid],
  )

  // Preload all images when the component mounts
  useEffect(() => {
    if (!isFilled.group(testimonials)) {
      return
    }

    testimonials.forEach((testimonial) => {
      if (testimonial.image.url) {
        const img = new Image()
        img.src = testimonial.image.url
      }
    })
  }, [testimonials])

  if (!isFilled.group(testimonials)) {
    return null
  }

  return (
    <div className="flex flex-col bg-moss-white pb-0 pt-10 lg:pt-20">
      <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-6 px-5 md:gap-12 md:px-10 lg:px-5 xl:px-0">
        <div className="flex flex-col gap-3 md:gap-6">
          <PrismicRichText
            field={slice.primary.tag}
            components={{
              paragraph: ({children}) => (
                <p className="text-tg-body-s md:text-button-s font-semibold uppercase text-moss-grey-400">{children}</p>
              ),
              strong: ({children}) => <strong className="pr-3 text-moss-blue">{children}</strong>,
            }}
          />
          <PrismicRichText
            components={{
              heading1: ({children}) => (
                <h2 className="md:text-tg-h2 text-[32px] font-normal leading-9 tracking-[-.32px] text-moss-grey-400 md:leading-[normal]">
                  {children}
                </h2>
              ),
              strong: ({children}) => (
                <>
                  <span className="md:text-tg-h2 text-[32px] leading-9 tracking-[-.32px] text-black md:leading-[normal]">
                    {children}
                  </span>
                  <br className="max-lg:hidden" />
                </>
              ),
            }}
            field={slice.primary.title}
          />
        </div>
        <div className="flex gap-2 max-md:pb-10">
          {testimonials.map((testimonial, index) => (
            <button
              key={index}
              onClick={() =>
                handleClick(
                  index,
                  {
                    event: EVENTS.CLICK,
                    location: `component-${componentIndex}-testimonials`,
                    pageName: context.pageUid,
                    mktgId: `testimonial-tab-${index}`,
                    dataType: 'testimonial-tab',
                  },
                  {
                    elementText: testimonial.tab_title!,
                  },
                )
              }
              className={cn(
                'md:text-button-s h-8 rounded-[4px] px-3 py-[9px] text-[11px] font-semibold uppercase tracking-[.66px]',
                {
                  'bg-green-neon text-black': index === activeTab,
                  'bg-moss-grey-100 text-moss-grey-500 hover:bg-moss-grey-150': index !== activeTab,
                },
              )}
            >
              {testimonial.tab_title}
            </button>
          ))}
        </div>
      </div>
      <div className="relative isolate grid w-full grid-cols-[minmax(20px,_1fr)_repeat(12,_minmax(auto,_100px))_minmax(20px,_1fr)] grid-rows-[auto_40px_168px_168px_168px_40px] md:grid-rows-[64px,532px,40px,168px,80px] md:max-lg:grid-cols-[minmax(40px,_1fr)_repeat(12,_minmax(auto,_100px))_minmax(40px,_1fr)]">
        <Gradient color="255, 255, 255" gradientStart={20} height={40} location="top" className="max-md:hidden" />

        {/* First row */}
        <div className="col-span-1 rounded-br-[4px] border-b border-r border-moss-grey-150 max-md:row-start-2" />
        <div className="col-span-12 ml-[-1px] rounded-b-[4px] border-x border-b border-moss-grey-150 max-md:relative max-md:row-start-2">
          <Gradient
            color="255, 255, 255"
            gradientStart={20}
            height={40}
            location="top"
            className="left-[-1px] w-[calc(100%+5px)] md:hidden"
          />
        </div>
        <div className="col-span-1 ml-[-1px] rounded-bl-[4px] border-b border-l border-moss-grey-150 max-md:row-start-2" />

        {/* Image section */}
        <div className="col-span-1 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150 max-md:col-start-1 max-md:row-start-3" />
        <div className="col-span-full ml-[-1px] mt-[-1px] flex flex-col items-center justify-center gap-6 overflow-hidden rounded-[4px] border border-moss-grey-150 max-md:relative max-md:h-[558px] max-md:border-none max-md:px-5 md:col-span-12">
          <div className="relative flex size-full flex-col p-10 max-md:overflow-hidden max-md:rounded-[8px]">
            <TestimonialBackground activeTab={activeTab} testimonials={testimonials} />
            <AnimatePresence mode="sync">
              <motion.div
                className="absolute inset-0 flex size-full flex-col justify-between p-6 md:p-10"
                key={activeTab}
                variants={contentVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <TestimonialContent
                  testimonial={testimonials[activeTab]}
                  activeTab={activeTab}
                  componentIndex={componentIndex}
                  context={context}
                  domainCountry={domainCountry}
                />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        <div className="col-span-1 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 max-md:col-start-[14] max-md:row-start-4" />

        {/* Divider section */}
        <div className="col-span-1 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150 max-md:col-start-1 max-md:row-start-4" />
        <div className="col-span-12 ml-[-1px] mt-[-1px] rounded-[4px] border border-moss-grey-150 max-md:col-start-2 max-md:row-start-6" />
        <div className="col-span-1 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 max-md:col-start-[14] max-md:row-start-3" />

        {/* Ratings section */}
        <div className="col-span-1 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150 max-md:row-start-5" />
        {ratings_section.map((rating, index) => (
          <div
            key={'ratings-' + index}
            // eslint-disable-next-line tailwindcss/no-custom-classname -- arbitrary value using the index
            className={`max-md:row-start- col-span-4 ml-[-1px] mt-[-1px] flex flex-col items-center justify-center gap-2 rounded-[4px] border border-moss-grey-150 p-4 max-md:col-start-2${
              index + 3
            } max-md:col-span-12`}
          >
            <PrismicRichText
              field={rating.content}
              components={{
                image: ({node}) => <img src={node.url} alt=" " className="size-8" />,
                heading5: ({children}) => (
                  <h5 className="text-tg-h3 text-center font-normal text-[#282828]">{children}</h5>
                ),
                em: ({children}) => (
                  <>
                    <span>{children}</span>
                    <br />
                  </>
                ),
                paragraph: ({children}) => (
                  <p className="text-detail text-center font-semibold uppercase leading-[normal] text-[#282828]/[60%]">
                    {children}
                  </p>
                ),
              }}
            />
          </div>
        ))}
        <div className="col-span-1 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150" />

        {/* Last row */}
        <div className="col-span-1 mt-[-1px] rounded-tr-[4px] border-r border-t border-moss-grey-150 max-md:row-start-6" />
        <div className="col-span-4 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150" />
        <div className="col-span-4 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150" />
        <div className="col-span-4 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150" />
        <div className="col-span-1 ml-[-1px] mt-[-1px] rounded-tl-[4px] border-l border-t border-moss-grey-150 max-md:row-start-6" />

        <Gradient color="255, 255, 255" gradientStart={20} height={40} location="bottom" />
      </div>
    </div>
  )
}

export default TestimonialsSectionDefault
